import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import API_BASE_URL from "../config";
import {
  vmPageShell,
  vmTableScrollWrap,
  vmRecordRowBg,
  vmRecordTable,
  getVmRecordTableStyle,
  getVmRecordThStyle,
  getVmRecordTdStyle,
  vmRecordColGroupReact,
} from "./vehicleManagementLayout";
import FleetVehicleSelect from "./FleetVehicleSelect";
import { FaEye } from "react-icons/fa";
import { ViewMediaEyeAnchor } from "../../Components/ViewMediaEye";

/** Date (yyyy-mm-dd) and time (HH:mm:ss) in Asia/Kolkata for form defaults. */
function getIstDateTimeStrings() {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date());
  const p = {};
  for (const { type, value } of parts) {
    if (type !== "literal") p[type] = value;
  }
  return {
    dateStr: `${p.year}-${p.month}-${p.day}`,
    timeStr: `${p.hour}:${p.minute}:${p.second}`,
  };
}

/** Parse saved diesel record date for filtering (local calendar). */
function parseRecordDate(record) {
  if (record?.date == null || record.date === "") return null;
  const d = record.date instanceof Date ? new Date(record.date) : new Date(record.date);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** yyyy-mm-dd in local timezone */
function toYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Expected distance (km) for one scheduled refill from Vehicle Onboarding only
 * (scheduleKmPerFill, or scheduleKmPerLitre × scheduleLitres). Ignores typed litres.
 */
function expectedKmTargetForFill(schedule) {
  if (!schedule) return null;
  if (schedule.scheduleKmPerFill != null && schedule.scheduleKmPerFill !== "")
    return Number(schedule.scheduleKmPerFill);
  const kmpl = parseFloat(schedule.scheduleKmPerLitre);
  const sl = parseFloat(schedule.scheduleLitres);
  if (Number.isFinite(kmpl) && sl > 0) return Math.round(kmpl * sl);
  return null;
}

/** If closing KM is still empty, set it to opening + onboarding expected km. */
function suggestClosingKmFromOnboarding(updated, schedule) {
  const targetKm = expectedKmTargetForFill(schedule);
  const start = parseFloat(updated.startKm);
  if (targetKm == null || !Number.isFinite(start) || start < 0) return;
  const endEmpty = updated.endKm === "" || updated.endKm == null;
  if (!endEmpty) return;
  updated.endKm = String(Math.round(start + targetKm));
}

/** Map GET /vehicle/diesel rows so Opening / Closing / Total show in the table. */
function normalizeDieselRecord(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const start =
    raw.startKm ??
    raw.openingKm ??
    raw.openKm ??
    raw.start_km ??
    "";
  const end =
    raw.endKm ??
    raw.closingKm ??
    raw.closeKm ??
    raw.end_km ??
    "";
  const openN = parseFloat(start);
  const closeN = parseFloat(end);
  let total = raw.totalKm ?? raw.totalKM ?? raw.runKm ?? "";
  if (total === "" || total == null) {
    if (Number.isFinite(openN) && Number.isFinite(closeN) && closeN >= openN) {
      total = closeN - openN;
    }
  }
  const asStr = (v) => {
    if (v == null || v === "") return "";
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
    const n = parseFloat(v);
    return Number.isFinite(n) ? String(n) : String(v).trim();
  };
  return {
    ...raw,
    startKm: asStr(start),
    endKm: asStr(end),
    totalKm: asStr(total),
  };
}

function fmtKmCell(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : String(v);
}

const DIESEL_RECORD_HEADERS = [
  "Date", "Time", "Vehicle No", "Vehicle Type", "Owner Name",
  "Schedule KM/L", "Schedule refill days", "Schedule Ltrs", "Expect KM",
  "Driver Name", "Opening KM", "Closing KM", "Total KM",
  "Fuel Station", "Actual Ltrs", "Price/L", "Total Amount",
  "Payment Mode", "Paid by",
  "Actual Km/L", "KM difference", "Performance",
  "Vehicle Route", "Diesel slip", "Remarks",
  "Supervisor disposition",
];

/** Pending refill verification table: column count for mobile fixed layout / colgroup. */
const PENDING_REFILL_COLUMN_COUNT = 25;

/**
 * Mobile: avoid shared 88px columns (crushes Actions). Wider data cols + fixed Actions width.
 */
const PREFILL_MOBILE_DATA_COL_PX = 108;
/** Wide enough for round action trigger + expanded 2+2+1 grid below. */
const PREFILL_MOBILE_ACTION_COL_PX = 300;
const PREFILL_MOBILE_TABLE_MIN_PX =
  (PENDING_REFILL_COLUMN_COUNT - 1) * PREFILL_MOBILE_DATA_COL_PX + PREFILL_MOBILE_ACTION_COL_PX;

/** Scoped styles: pending refill grid scroll, row hover, action buttons. */
const DIESEL_VM_PREFILL_CSS = `
.diesel-vm-prefill-scroll{scrollbar-color:#bdbdbd #ececec;scrollbar-width:thin;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}
.diesel-vm-prefill-scroll::-webkit-scrollbar{height:12px;width:12px}
.diesel-vm-prefill-scroll::-webkit-scrollbar-track{background:#ececec;border-radius:6px}
.diesel-vm-prefill-scroll::-webkit-scrollbar-thumb{background:#b0b0b0;border-radius:6px;border:2px solid #ececec}
.diesel-vm-prefill-scroll::-webkit-scrollbar-thumb:hover{background:#9a9a9a}
tr.diesel-vm-prefill-row:hover{background-color:#fff4d6!important;box-shadow:inset 0 0 0 1px rgba(201,164,0,0.28)}
tr.diesel-vm-prefill-row a{color:#0d47a1;text-decoration:underline}
tr.diesel-vm-prefill-row a:hover{color:#1565c0}
.diesel-vm-prefill-btn{font-family:Arial,sans-serif;font-size:12px;font-weight:700;padding:9px 12px;border-radius:7px;cursor:pointer;border:none;letter-spacing:0.02em;transition:transform .12s ease,box-shadow .12s ease,filter .12s ease,background-color .12s ease;box-shadow:0 1px 3px rgba(0,0,0,.16),0 0 0 1px rgba(0,0,0,.04);min-height:44px;touch-action:manipulation;-webkit-tap-highlight-color:rgba(0,0,0,.08)}
.diesel-vm-prefill-btn:focus-visible{outline:2px solid #1a1a1a;outline-offset:2px}
.diesel-vm-prefill-btn--y:focus-visible{outline-color:#5d4037}
.diesel-vm-prefill-btn:disabled{opacity:.52;cursor:not-allowed;transform:none!important;box-shadow:none!important;filter:none!important}
.diesel-vm-prefill-btn--y{background:#FFC107;color:#111}
.diesel-vm-prefill-btn--y:hover:not(:disabled){background:#ffcd38;box-shadow:0 2px 7px rgba(0,0,0,.2),0 0 0 1px rgba(0,0,0,.06);transform:translateY(-1px)}
.diesel-vm-prefill-btn--y:active:not(:disabled){transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,.14)}
.diesel-vm-prefill-btn--b{background:#1a1a1a;color:#fff}
.diesel-vm-prefill-btn--b:hover:not(:disabled){background:#2e2e2e;box-shadow:0 2px 7px rgba(0,0,0,.3),0 0 0 1px rgba(255,255,255,.06);transform:translateY(-1px)}
.diesel-vm-prefill-btn--b:active:not(:disabled){transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,.14)}
.diesel-vm-prefill-actions{display:grid;grid-template-columns:repeat(2,minmax(min-content,1fr));align-items:stretch;justify-items:stretch;width:100%;box-sizing:border-box;column-gap:10px;row-gap:8px}
.diesel-vm-prefill-actions .diesel-vm-prefill-btn{width:100%;box-sizing:border-box;white-space:nowrap;justify-self:stretch}
.diesel-vm-prefill-actions > .diesel-vm-prefill-btn:nth-child(5){grid-column:1/-1;justify-self:stretch;text-align:center}
.diesel-vm-prefill-actions-shell{width:100%;box-sizing:border-box;display:flex;flex-direction:column;align-items:center;gap:0}
.diesel-vm-prefill-actions-panel{width:100%;max-width:320px;overflow:hidden;max-height:0;opacity:0;margin-top:0;padding:0 6px;box-sizing:border-box;pointer-events:none;border-radius:8px;border:1px solid transparent;background:transparent;transition:max-height .28s ease,opacity .22s ease,margin .22s ease,padding .22s ease,border-color .22s ease,background .22s ease,box-shadow .22s ease}
.diesel-vm-prefill-actions-panel--open{max-height:320px;opacity:1;margin-top:8px;padding:8px 6px;pointer-events:auto;border-color:#d4c896;background:#fffdf8;box-shadow:0 6px 16px rgba(0,0,0,.12),0 0 0 1px rgba(0,0,0,.04)}
.diesel-vm-prefill-actions-trigger{position:relative;appearance:none;font-family:Arial,sans-serif;font-size:12px;font-weight:700;letter-spacing:.02em;display:inline-flex;align-items:center;justify-content:center;min-width:44px;min-height:44px;padding:9px 12px;border-radius:7px;cursor:pointer;border:none;background:#FFC107;color:#111;box-shadow:0 1px 3px rgba(0,0,0,.16),0 0 0 1px rgba(0,0,0,.04);touch-action:manipulation;-webkit-tap-highlight-color:rgba(0,0,0,.08);transition:transform .12s ease,box-shadow .12s ease,background-color .12s ease}
.diesel-vm-prefill-actions-trigger svg{width:16px;height:16px;flex-shrink:0;color:#111}
.diesel-vm-prefill-actions-trigger:hover:not(:disabled){background:#f0b800;box-shadow:0 2px 7px rgba(0,0,0,.2),0 0 0 1px rgba(0,0,0,.06);transform:translateY(-1px)}
.diesel-vm-prefill-actions-trigger:active:not(:disabled){transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,.14)}
.diesel-vm-prefill-actions-trigger--open{background:#f0b800;color:#111;box-shadow:0 0 0 2px #1a1a1a,0 2px 7px rgba(0,0,0,.18)}
.diesel-vm-prefill-actions-trigger:disabled{opacity:.52;cursor:not-allowed;transform:none!important;box-shadow:0 1px 3px rgba(0,0,0,.1)!important}
.diesel-vm-prefill-actions-trigger:focus-visible{outline:2px solid #1a1a1a;outline-offset:2px}
.diesel-vm-prefill-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
.diesel-vm-prefill-empty{margin:6px 0 0;padding:18px 16px;text-align:center;border:1px solid #e0e0e0;border-radius:8px;background:linear-gradient(180deg,#fafafa 0%,#f3f3f4 100%);color:#4a4a4a;font-size:13px;line-height:1.45}
.diesel-vm-prefill-loading{display:inline-flex;align-items:center;gap:8px;color:#444;font-size:13px;padding:4px 2px}
.diesel-vm-prefill-loading::before{content:"";width:14px;height:14px;border:2px solid #ccc;border-top-color:#FFC107;border-radius:50%;animation:diesel-vm-prefill-spin .7s linear infinite}
@keyframes diesel-vm-prefill-spin{to{transform:rotate(360deg)}}
`.trim();

/** Mobile colgroup: last column reserved for Actions (not 88px shared grid). */
function DieselPrefillMobileColGroup() {
  const n = PENDING_REFILL_COLUMN_COUNT - 1;
  return (
    <colgroup>
      {Array.from({ length: n }, (_, i) => (
        <col
          key={i}
          style={{
            width: PREFILL_MOBILE_DATA_COL_PX,
            minWidth: PREFILL_MOBILE_DATA_COL_PX,
          }}
        />
      ))}
      <col
        style={{
          width: PREFILL_MOBILE_ACTION_COL_PX,
          minWidth: PREFILL_MOBILE_ACTION_COL_PX,
        }}
      />
    </colgroup>
  );
}

/** Sort key: latest diesel fill first (date + time). */
function dieselRecordSortKey(r) {
  const d = parseRecordDate(r);
  const base = d ? d.getTime() : 0;
  const t = String(r.time || "00:00:00").split(":");
  const h = parseInt(t[0], 10) || 0;
  const m = parseInt(t[1], 10) || 0;
  const s = parseInt(t[2], 10) || 0;
  return base + (h * 3600 + m * 60 + s) * 1000;
}

/** Closing odometer for a saved row (prefers endKm; else startKm + totalKm). */
function closingKmForRecord(r) {
  const row = normalizeDieselRecord(r);
  const end = parseFloat(row.endKm);
  if (Number.isFinite(end) && end >= 0) return end;
  const start = parseFloat(row.startKm);
  const tot = parseFloat(row.totalKm);
  if (Number.isFinite(start) && Number.isFinite(tot) && tot >= 0) return start + tot;
  return NaN;
}

/** Latest saved closing KM for this vehicle (next fill opens with that as opening). */
function lastClosingKmFromRecords(records, vehicleNo) {
  if (!vehicleNo || !Array.isArray(records)) return null;
  const v = vehicleNo.trim().toUpperCase();
  const matches = records
    .map((r) => normalizeDieselRecord(r))
    .filter((r) => (r.vehicleNumber || "").trim().toUpperCase() === v);
  const withClose = matches.filter((r) => {
    const c = closingKmForRecord(r);
    return Number.isFinite(c) && c >= 0;
  });
  if (!withClose.length) return null;
  withClose.sort((a, b) => dieselRecordSortKey(b) - dieselRecordSortKey(a));
  const n = closingKmForRecord(withClose[0]);
  return Number.isFinite(n) ? String(Math.round(n)) : null;
}

function createInitialForm() {
  const { dateStr, timeStr } = getIstDateTimeStrings();
  return {
    date: dateStr,
    time: timeStr,
    vehicleNumber: "",
    vehicleType: "Truck",
    ownerName: "",
    driverName: "",
    pumpName: "",
    startKm: "",
    endKm: "",
    totalKm: "",
    volume: "",
    ratePerLiter: "",
    totalAmount: "",
    paymentMode: "Cash",
    paidBy: "",
    vehicleRoute: "",
    remarks: "",
    dieselSlipPhoto: "",
  };
}

const Diesel = () => {
  const [formData, setFormData] = useState(() => createInitialForm());
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  /** Records table: day | month | year | range | all */
  const [filterMode, setFilterMode] = useState("all");
  const [filterDay, setFilterDay] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterVehicleNo, setFilterVehicleNo] = useState("");
  /** When true, diesel saved from the admin form (not an approved supervisor refill) is hidden from the list. */
  const [hideDirectDieselEntries, setHideDirectDieselEntries] = useState(true);
  /** Full entry form is hidden until user clicks “Add fields”; closes again after successful save. */
  const [entryFormOpen, setEntryFormOpen] = useState(false);

  /** After user edits Time, stop overwriting every second until Clear / successful Save. */
  const timeManualRef = useRef(false);
  const slipPhotoRef = useRef(null);

  const [scheduleMap, setScheduleMap] = useState({});
  const [pendingRefills, setPendingRefills] = useState([]);
  const [pendingBusy, setPendingBusy] = useState(false);
  /** `{ id, action }` while a PATCH is in flight (disables row; shows Working… on the clicked action). */
  const [pendingAction, setPendingAction] = useState(null);
  /** Mobile: tap round control to reveal five approval actions (desktop: always show five). */
  const [prefillActionsOpenId, setPrefillActionsOpenId] = useState(null);

  const mergeVehicleIntoFormData = useCallback(
    (prev, value) => {
      const updated = { ...prev, vehicleNumber: value };
      const sc = value ? scheduleMap[value] : null;
      updated.ownerName = sc ? (sc.ownerName || "").trim() : "";
      if (sc?.vehicleType) updated.vehicleType = sc.vehicleType;
      const sl = parseFloat(sc?.scheduleLitres);
      if (sc && Number.isFinite(sl) && sl > 0) {
        updated.volume = String(sl);
        const rate = parseFloat(updated.ratePerLiter);
        if (Number.isFinite(rate) && rate > 0) {
          updated.totalAmount = (sl * rate).toFixed(2);
        }
      }
      updated.endKm = "";
      const prevClose = lastClosingKmFromRecords(records, value);
      if (prevClose != null) {
        updated.startKm = prevClose;
      } else {
        updated.startKm = "";
      }
      suggestClosingKmFromOnboarding(updated, sc);
      updated.totalKm = Math.max(
        0,
        (parseFloat(updated.endKm) || 0) - (parseFloat(updated.startKm) || 0)
      ).toString();
      return updated;
    },
    [scheduleMap, records]
  );

  useEffect(() => {
    if (!entryFormOpen) return undefined;
    const tick = () => {
      if (timeManualRef.current) return;
      const { timeStr } = getIstDateTimeStrings();
      setFormData((prev) => ({ ...prev, time: timeStr }));
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [entryFormOpen]);

  const currentSched = scheduleMap[formData.vehicleNumber] || null;

  const expectedKm =
    currentSched?.scheduleKmPerFill
      ? Number(currentSched.scheduleKmPerFill)
      : currentSched?.scheduleKmPerLitre
        ? (() => {
            const kmpl = parseFloat(currentSched.scheduleKmPerLitre);
            if (!Number.isFinite(kmpl)) return null;
            const vol = parseFloat(formData.volume);
            if (vol > 0) return Math.round(kmpl * vol);
            const sl = parseFloat(currentSched.scheduleLitres);
            if (sl > 0) return Math.round(kmpl * sl);
            return null;
          })()
        : null;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const S = {
    container: {
      ...vmPageShell(isMobile),
      fontFamily: "Arial, sans-serif",
      backgroundColor: "#f0f0f1",
      color: "black",
      minHeight: "calc(100vh - 70px)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
    },
    pageHeader: {
      backgroundColor: "#FFC107", color: "#000", padding: "16px 20px",
      fontWeight: "bold", fontSize: "20px", letterSpacing: "1px", textTransform: "uppercase",
    },
    innerPad: { padding: isMobile ? "14px" : "20px", backgroundColor: "#f0f0f1" },
    sectionTitle: {
      fontWeight: "bold", fontSize: "14px", color: "#000",
      borderBottom: "2px solid #FFC107", paddingBottom: "6px",
      marginBottom: "16px", marginTop: "20px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(240px, 1fr))",
      gap: "16px", marginBottom: "16px",
    },
    label: { fontWeight: "bold", fontSize: "13px", marginBottom: "5px", display: "block", color: "#000" },
    input: {
      width: "100%", padding: "9px 10px", border: "1.5px solid #000", borderRadius: "4px",
      fontSize: isMobile ? "16px" : "14px", boxSizing: "border-box",
      color: "#000", backgroundColor: "#fff", outline: "none",
    },
    readOnly: {
      width: "100%", padding: "9px 10px", border: "1.5px solid #ccc", borderRadius: "4px",
      fontSize: isMobile ? "16px" : "14px", boxSizing: "border-box",
      color: "#555", backgroundColor: "#f9f9f9", outline: "none",
    },
    buttonRow: {
      display: "flex", gap: "12px", justifyContent: "flex-end",
      paddingBottom: "10px", flexWrap: "wrap",
    },
    btnBlack: {
      padding: "9px 28px", border: "none", borderRadius: "4px",
      cursor: "pointer", backgroundColor: "black", color: "white",
      fontWeight: "bold", fontSize: "14px",
    },
    btnYellow: {
      padding: "9px 28px", border: "none", borderRadius: "4px",
      cursor: "pointer", backgroundColor: "#FFC107", color: "black",
      fontWeight: "bold", fontSize: "14px",
    },
    tableTopRow: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: "12px", flexWrap: "wrap", gap: "10px",
    },
    searchInput: {
      padding: "8px 12px", border: "1.5px solid #000", borderRadius: "4px",
      fontSize: "14px", width: "280px", maxWidth: "100%", color: "#000",
    },
    divider: { borderTop: "2px solid #f0f0f0", margin: "24px 0" },
    table: getVmRecordTableStyle(isMobile, DIESEL_RECORD_HEADERS.length),
    th: getVmRecordThStyle(isMobile),
    td: { ...getVmRecordTdStyle(isMobile), color: "#000" },
    refillTable: {
      ...(isMobile
        ? {
            ...vmRecordTable,
            tableLayout: "fixed",
            width: `${PREFILL_MOBILE_TABLE_MIN_PX}px`,
            minWidth: `${PREFILL_MOBILE_TABLE_MIN_PX}px`,
            fontSize: "12px",
          }
        : getVmRecordTableStyle(false, PENDING_REFILL_COLUMN_COUNT)),
      ...(isMobile ? {} : { minWidth: "1980px" }),
      backgroundColor: "#fff",
      border: "1px solid #e4e4e4",
      boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
    },
    refillTh: {
      ...getVmRecordThStyle(isMobile),
      ...(isMobile
        ? {
            whiteSpace: "nowrap",
            wordBreak: "normal",
            overflowWrap: "normal",
            position: "sticky",
            top: 0,
            zIndex: 6,
            verticalAlign: "middle",
            lineHeight: 1.3,
          }
        : {}),
      borderRight: "1px solid rgba(0,0,0,0.06)",
      boxShadow: isMobile ? "0 2px 4px rgba(0,0,0,0.06)" : "0 2px 4px rgba(0,0,0,0.06)",
    },
    refillTd: {
      ...getVmRecordTdStyle(isMobile),
      ...(isMobile
        ? {
            whiteSpace: "nowrap",
            wordBreak: "normal",
            overflowWrap: "normal",
            verticalAlign: "middle",
            lineHeight: 1.35,
          }
        : {}),
      borderRight: "1px solid #efefef",
      borderBottom: "1px solid #ebebeb",
      color: "#1a1a1a",
    },
    refillActionCell: {
      minWidth: isMobile ? `${PREFILL_MOBILE_ACTION_COL_PX}px` : "210px",
      width: isMobile ? `${PREFILL_MOBILE_ACTION_COL_PX}px` : undefined,
      maxWidth: isMobile ? `${PREFILL_MOBILE_ACTION_COL_PX}px` : "248px",
      textAlign: "center",
      verticalAlign: "middle",
      backgroundColor: "#fafafa",
    },
    refillActionsWrap: {
      width: "100%",
      maxWidth: isMobile ? "100%" : "216px",
      margin: "0 auto",
      padding: isMobile ? "10px 8px" : "6px 4px",
      boxSizing: "border-box",
    },
    prefillVerifyPanel: {
      marginTop: entryFormOpen ? "16px" : "12px",
      marginBottom: "4px",
      padding: isMobile ? "12px 12px 14px" : "14px 16px 18px",
      backgroundColor: "#ebebed",
      borderRadius: "8px",
      border: "1px solid #dedee0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    prefillDashedCard: {
      padding: isMobile ? "12px 12px" : "16px 18px",
      backgroundColor: "#fffef8",
      border: "1px dashed #c9a400",
      borderRadius: "8px",
      fontSize: "14px",
      color: "#2a2a2a",
      fontFamily: "inherit",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    },
    prefillScrollArea: {
      ...vmTableScrollWrap,
      marginTop: "4px",
      border: "1px solid #dcdcdc",
      borderRadius: "8px",
      backgroundColor: "#fff",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03), 0 2px 10px rgba(0,0,0,0.05)",
      maxWidth: "100%",
      ...(isMobile ? { touchAction: "pan-x pan-y" } : {}),
    },
  };

  // ── Data fetching ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRecords();
    fetchVehicleSchedules();
    fetchPendingRefills();
  }, []);

  /** Next fill: opening KM must equal the previous trip’s closing (from records). Re-applies when records refresh. */
  useEffect(() => {
    if (!entryFormOpen) return;
    setFormData((prev) => {
      const vn = (prev.vehicleNumber || "").trim();
      if (!vn) return prev;
      const want = lastClosingKmFromRecords(records, vn);
      if (want == null) return prev;
      const sc = scheduleMap[vn] || null;
      const next = { ...prev, startKm: want };
      next.endKm = "";
      suggestClosingKmFromOnboarding(next, sc);
      next.totalKm = String(
        Math.max(0, (parseFloat(next.endKm) || 0) - (parseFloat(next.startKm) || 0))
      );
      const same =
        String(prev.startKm || "").trim() === String(next.startKm || "").trim() &&
        String(prev.endKm || "").trim() === String(next.endKm || "").trim() &&
        String(prev.totalKm || "").trim() === String(next.totalKm || "").trim();
      return same ? prev : next;
    });
  }, [records, entryFormOpen, scheduleMap]);

  const fetchRecords = async () => {
    try {
      /** Only admin direct saves + approved/confirmed refills — not pending supervisor requests. */
      const res = await axios.get(API_BASE_URL + "/vehicle/diesel", {
        params: { officialOnly: "1" },
      });
      const list = res.data || [];
      setRecords(Array.isArray(list) ? list.map(normalizeDieselRecord) : []);
    } catch (e) { console.error("Error fetching diesel data:", e); }
  };

  const fetchVehicleSchedules = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/onboarding/all-vehicles");
      const vehicles = res.data || [];
      const map = {};
      vehicles.forEach(v => {
        if (v.vehicleNumber) {
          const regName = v.registerName != null ? String(v.registerName).trim() : "";
          const legacyOwner = v.ownerName != null ? String(v.ownerName).trim() : "";
          map[v.vehicleNumber] = {
            /** Matches Vehicle Onboarding “Register name” (stored as registerName on vehicle). */
            ownerName: regName || legacyOwner,
            vehicleType:        v.vehicleType,
            scheduleInterval:   v.scheduleInterval,
            scheduleLitres:     v.scheduleLitres,
            scheduleKmPerLitre: v.scheduleKmPerLitre,
            scheduleKmPerFill:  v.scheduleKmPerFill,
            scheduleKmActual:   v.scheduleKmActual,
            scheduleDate:       v.scheduleDate,
            scheduleDay:        v.scheduleDay,
          };
        }
      });
      setScheduleMap(map);
    } catch (e) { console.error("Error fetching vehicle schedules:", e); }
  };

  const fetchPendingRefills = async () => {
    try {
      setPendingBusy(true);
      const res = await axios.get(`${API_BASE_URL}/vehicle/refill-requests`, {
        params: { status: "PENDING" },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      // Guard against accidental duplicate submissions: keep latest by signature.
      const bySig = new Map();
      for (const r of rows) {
        const sig = [
          String(r.vehicleNumber || "").trim().toUpperCase(),
          String(r.date || ""),
          String(r.time || ""),
          String(r.driverName || "").trim().toLowerCase(),
          String(r.pumpName || "").trim().toLowerCase(),
          String(r.volume ?? ""),
          String(r.totalAmount ?? ""),
          String(r.startKm ?? ""),
        ].join("|");
        const prev = bySig.get(sig);
        if (!prev || new Date(r.createdAt || 0).getTime() > new Date(prev.createdAt || 0).getTime()) {
          bySig.set(sig, r);
        }
      }
      setPendingRefills([...bySig.values()]);
    } catch (e) {
      console.error("Error fetching pending refill requests:", e);
      setPendingRefills([]);
    } finally {
      setPendingBusy(false);
    }
  };

  const updatePendingRefill = async (id, action) => {
    if (!id || !action) return;
    setPrefillActionsOpenId(null);
    try {
      setPendingAction({ id, action });
      await axios.patch(`${API_BASE_URL}/vehicle/refill-requests/${id}`, {
        action,
        reviewedBy: "ADMIN_UI",
      });
      await fetchPendingRefills();
      if (action === "approve" || action === "confirm") {
        await fetchRecords();
      }
    } catch (e) {
      console.error(`Error trying to ${action} refill request:`, e);
      alert(
        `Could not ${action} this request. ` +
          (e?.response?.data?.message || e.message || "Please try again."),
      );
    } finally {
      setPendingAction(null);
    }
  };

  /** Close actions panel on outside tap / Escape. */
  useEffect(() => {
    if (prefillActionsOpenId == null) return undefined;
    const close = (e) => {
      const t = e.target;
      if (typeof t.closest === "function" && t.closest("[data-prefill-actions-wrap]")) return;
      setPrefillActionsOpenId(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPrefillActionsOpenId(null);
    };
    document.addEventListener("mousedown", close, false);
    document.addEventListener("touchstart", close, false);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", close, false);
      document.removeEventListener("touchstart", close, false);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [prefillActionsOpenId]);

  // ── Auto-fill ownerName when vehicle is selected ─────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "time") timeManualRef.current = true;

    if (name === "vehicleNumber") {
      setFormData((prev) => mergeVehicleIntoFormData(prev, value));
      return;
    }

    const updated = { ...formData, [name]: value };

    if (name === "startKm") {
      suggestClosingKmFromOnboarding(updated, scheduleMap[updated.vehicleNumber]);
    }

    if (name === "startKm" || name === "endKm") {
      updated.totalKm = Math.max(
        0,
        (parseFloat(updated.endKm) || 0) - (parseFloat(updated.startKm) || 0)
      ).toString();
    }
    if (name === "volume" || name === "ratePerLiter") {
      updated.totalAmount = (
        (parseFloat(updated.volume) || 0) *
        (parseFloat(updated.ratePerLiter) || 0)
      ).toFixed(2);
      if (name === "volume" && updated.vehicleNumber) {
        updated.endKm = "";
        suggestClosingKmFromOnboarding(updated, scheduleMap[updated.vehicleNumber]);
      }
      updated.totalKm = String(
        Math.max(0, (parseFloat(updated.endKm) || 0) - (parseFloat(updated.startKm) || 0))
      );
    }
    setFormData(updated);
  };

  const handleSlipPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file for the diesel slip.");
      return;
    }
    const maxBytes = 2.5 * 1024 * 1024;
    if (file.size > maxBytes) {
      alert("Diesel slip image must be under 2.5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, dieselSlipPhoto: reader.result || "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.vehicleNumber || "").trim()) {
      alert("Please select a vehicle from the list.");
      return;
    }
    const payload = { ...formData };
    const sc = scheduleMap[payload.vehicleNumber] || null;
    suggestClosingKmFromOnboarding(payload, sc);
    payload.totalKm = String(
      Math.max(0, (parseFloat(payload.endKm) || 0) - (parseFloat(payload.startKm) || 0))
    );
    try {
      await axios.post(API_BASE_URL + "/vehicle/diesel", payload);
      alert("Diesel entry saved successfully!");
      timeManualRef.current = false;
      if (slipPhotoRef.current) slipPhotoRef.current.value = "";
      setFormData(createInitialForm());
      setEntryFormOpen(false);
      fetchRecords();
    } catch (e) {
      console.error("Error saving diesel entry:", e);
      alert("Error: " + (e?.response?.data?.message || e.message || "Failed to save. Check backend connection."));
    }
  };

  const handleClear = () => {
    timeManualRef.current = false;
    if (slipPhotoRef.current) slipPhotoRef.current.value = "";
    setFormData(createInitialForm());
  };

  const openEntryForm = () => {
    timeManualRef.current = false;
    if (slipPhotoRef.current) slipPhotoRef.current.value = "";
    setFormData(createInitialForm());
    setEntryFormOpen(true);
  };

  const handleCancelEntry = () => {
    handleClear();
    setEntryFormOpen(false);
  };

  const downloadCSV = () => {
    axios.get(API_BASE_URL + "/vehicle/diesel-csv", { responseType: "blob" })
      .then((res) => saveAs(res.data, "DieselData.csv"))
      .catch((e) => console.error("CSV download error:", e));
  };

  const clearRecordFilters = () => {
    setFilterMode("all");
    setFilterDay("");
    setFilterMonth("");
    setFilterYear("");
    setFilterFrom("");
    setFilterTo("");
    setFilterVehicleNo("");
    setSearchTerm("");
    setHideDirectDieselEntries(true);
  };

  const yearOptions = useMemo(() => {
    const ys = new Set();
    const cy = new Date().getFullYear();
    ys.add(cy);
    records.forEach((r) => {
      const d = parseRecordDate(r);
      if (d) ys.add(d.getFullYear());
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [records]);

  const filtered = records
    .filter((r) => {
    const driver = (r.driverName || "").toLowerCase();
    const veh = (r.vehicleNumber || "").toLowerCase();
    const q = searchTerm.trim().toLowerCase();
    if (q && !driver.includes(q) && !veh.includes(q)) return false;

    const fv = filterVehicleNo.trim().toLowerCase();
    if (fv && !veh.includes(fv)) return false;

    if (filterMode === "all") return true;

    const d = parseRecordDate(r);
    if (!d) return false;

    if (filterMode === "day") {
      if (!filterDay) return true;
      return toYmdLocal(d) === filterDay;
    }
    if (filterMode === "month") {
      if (!filterMonth) return true;
      const parts = filterMonth.split("-").map(Number);
      const yy = parts[0];
      const mm = parts[1];
      return d.getFullYear() === yy && d.getMonth() === mm - 1;
    }
    if (filterMode === "year") {
      if (!filterYear) return true;
      return d.getFullYear() === Number(filterYear);
    }
    if (filterMode === "range") {
      const t = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      if (filterFrom) {
        const [fy, fm, fd] = filterFrom.split("-").map(Number);
        const fromT = new Date(fy, fm - 1, fd).getTime();
        if (t < fromT) return false;
      }
      if (filterTo) {
        const [ty, tm, td] = filterTo.split("-").map(Number);
        const toT = new Date(ty, tm - 1, td).getTime();
        if (t > toT) return false;
      }
      return true;
    }
    return true;
  })
    .filter((r) => {
      if (!hideDirectDieselEntries) return true;
      const src = String(r.dieselSource || "").trim().toUpperCase();
      const disp = String(r.supervisorDisposition || "").trim().toLowerCase();
      if (src === "ADMIN_FORM" || disp === "direct entry") return false;
      return true;
    });

  const recMileage = (r) =>
    r.volume > 0 && r.totalKm > 0
      ? (r.totalKm / r.volume).toFixed(2)
      : "—";

  const recExpKm = (r) => {
    const sc = scheduleMap[r.vehicleNumber];
    if (!sc) return null;
    if (sc.scheduleKmPerFill) return Number(sc.scheduleKmPerFill);
    const kmpl = parseFloat(sc.scheduleKmPerLitre);
    if (!Number.isFinite(kmpl)) return null;
    const vol = parseFloat(r.volume);
    if (vol > 0) return Math.round(kmpl * vol);
    const sl = parseFloat(sc.scheduleLitres);
    if (sl > 0) return Math.round(kmpl * sl);
    return null;
  };

  /** Actual km/L minus schedule km/L (same as form “performance”). */
  const recPerfKmL = (r) => {
    const sc = scheduleMap[r.vehicleNumber];
    const aKmL = recMileage(r);
    if (aKmL === "—" || !sc?.scheduleKmPerLitre) return "—";
    const d = parseFloat(aKmL) - parseFloat(sc.scheduleKmPerLitre);
    if (!Number.isFinite(d)) return "—";
    return `${d >= 0 ? "+" : ""}${d.toFixed(2)}`;
  };

  return (
    <div style={S.container}>
      <style>{DIESEL_VM_PREFILL_CSS}</style>
      <div style={S.pageHeader}>DIESEL MANAGEMENT</div>

      <div style={S.innerPad}>
        <div style={S.sectionTitle}>Entry</div>
        {!entryFormOpen ? (
          <div style={{ marginBottom: "20px" }}>
            <button type="button" style={S.btnYellow} onClick={openEntryForm}>
              Add Diesel Data
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={S.sectionTitle}>Details</div>
          <div style={S.formGrid}>

            <div>
              <label style={S.label}>Date:</label>
              <input type="date" style={S.input} name="date"
                value={formData.date} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Time:</label>
              <input type="time" step={1} style={S.input} name="time"
                value={formData.time} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Vehicle No:</label>
              <FleetVehicleSelect
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                style={S.input}
                required
              />
            </div>

            <div>
              <label style={S.label}>Vehicle type:</label>
              <select style={S.input} name="vehicleType"
                value={formData.vehicleType} onChange={handleChange} required>
                <optgroup label="── Fleet Trucks ──">
                  <option value="Pickup Truck">Pickup Truck</option>
                  <option value="407 Truck">407 Truck</option>
                  <option value="17FT">17FT</option>
                  <option value="20FT">20FT</option>
                  <option value="Truck">Truck</option>
                </optgroup>
                <optgroup label="── Other ──">
                  <option value="Bus">Bus</option>
                  <option value="Car">Car</option>
                  <option value="Tempo">Tempo</option>
                  <option value="Bike">Bike</option>
                  <option value="Other">Other</option>
                </optgroup>
              </select>
            </div>

            <div>
              <label style={S.label}>Owner name:</label>
              <input type="text" style={S.input} name="ownerName"
                value={formData.ownerName} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Schedule mileage per/L:</label>
              <input type="text" style={S.readOnly} readOnly
                value={currentSched?.scheduleKmPerLitre != null && currentSched.scheduleKmPerLitre !== ""
                  ? `${currentSched.scheduleKmPerLitre} km/L`
                  : "—"} />
            </div>

            <div>
              <label style={S.label}>Schedule refill days:</label>
              <input type="text" style={S.readOnly} readOnly tabIndex={-1}
                value={currentSched?.scheduleInterval != null && currentSched.scheduleInterval !== ""
                  ? `${currentSched.scheduleInterval} days`
                  : "—"} />
            </div>

            <div>
              <label style={S.label}>Schedule Ltrs:</label>
              <input type="text" style={S.readOnly} readOnly
                value={currentSched?.scheduleLitres != null && currentSched.scheduleLitres !== ""
                  ? `${currentSched.scheduleLitres} L`
                  : "—"} />
            </div>

            <div>
              <label style={S.label}>Expect KM:</label>
              <input type="text" style={S.readOnly} readOnly
                value={expectedKm != null ? `${expectedKm} km` : "—"} />
            </div>

            <div>
              <label style={S.label}>Driver name:</label>
              <input type="text" style={S.input} name="driverName"
                value={formData.driverName} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Opening KM:</label>
              <input type="number" style={S.input} name="startKm"
                value={formData.startKm} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Fuel station / location:</label>
              <input type="text" style={S.input} name="pumpName"
                value={formData.pumpName} onChange={handleChange} required />
            </div>

            <div>
              <label style={S.label}>Actual Ltrs:</label>
              <input type="number" style={S.input} name="volume"
                value={formData.volume} onChange={handleChange} required
                step="0.01" />
            </div>

            <div>
              <label style={S.label}>Price/L:</label>
              <input type="number" style={S.input} name="ratePerLiter"
                value={formData.ratePerLiter} onChange={handleChange} required
                step="0.01" />
            </div>

            <div>
              <label style={S.label}>Total amount:</label>
              <input type="number" style={S.readOnly} name="totalAmount"
                value={formData.totalAmount} readOnly tabIndex={-1} />
            </div>

            <div>
              <label style={S.label}>Payment mode:</label>
              <select style={S.input} name="paymentMode"
                value={formData.paymentMode} onChange={handleChange} required>
                {["Cash", "OTP", "UPI", "Bank"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            <div>
              <label style={S.label}>Paid by:</label>
              <input type="text" style={S.input} name="paidBy"
                value={formData.paidBy} onChange={handleChange} required />
            </div>
          </div>

          <div style={S.sectionTitle}>Additional</div>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Vehicle route:</label>
              <input type="text" style={S.input} name="vehicleRoute"
                value={formData.vehicleRoute} onChange={handleChange} />
            </div>

            <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
              <label style={S.label}>Remarks:</label>
              <input type="text" style={S.input} name="remarks"
                value={formData.remarks} onChange={handleChange} />
            </div>

            <div style={{ gridColumn: isMobile ? undefined : "1 / -1" }}>
              <label style={S.label}>Diesel slip (photo):</label>
              <input ref={slipPhotoRef} type="file" accept="image/*" style={S.input}
                onChange={handleSlipPhoto} />
              {formData.dieselSlipPhoto ? (
                <div style={{ marginTop: "8px" }}>
                  <img src={formData.dieselSlipPhoto} alt="Diesel slip preview" style={{ maxWidth: "100%", maxHeight: "160px", border: "1px solid #ccc", borderRadius: "4px" }} />
                </div>
              ) : null}
            </div>
          </div>

          <div style={S.buttonRow}>
            <button type="button" style={S.btnBlack} onClick={handleCancelEntry}>Cancel</button>
            <button type="button" style={S.btnBlack} onClick={handleClear}>Clear</button>
            <button type="submit" style={S.btnYellow}>Save Entry</button>
          </div>
        </form>
        )}

        <div style={S.prefillVerifyPanel}>
          <div style={S.sectionTitle}>New Refill Verifies</div>
          <div style={S.prefillDashedCard}>
            {pendingBusy ? (
              <div className="diesel-vm-prefill-loading" role="status" aria-live="polite">
                Loading pending refills
              </div>
            ) : pendingRefills.length > 0 ? (
              <div className="diesel-vm-prefill-scroll" style={S.prefillScrollArea}>
                <table style={S.refillTable}>
                  {isMobile ? <DieselPrefillMobileColGroup /> : null}
                  <thead>
                    <tr>
                      <th style={S.refillTh}>Date</th>
                      <th style={S.refillTh}>Time</th>
                      <th style={S.refillTh}>Vehicle</th>
                      <th style={S.refillTh}>Vehicle Type</th>
                      <th style={S.refillTh}>Owner</th>
                      <th style={S.refillTh}>Sch KM/L</th>
                      <th style={S.refillTh}>Sch Days</th>
                      <th style={S.refillTh}>Sch Ltrs</th>
                      <th style={S.refillTh}>Expect KM</th>
                      <th style={S.refillTh}>Driver</th>
                      <th style={S.refillTh}>Opening KM</th>
                      <th style={S.refillTh}>Closing KM</th>
                      <th style={S.refillTh}>Total KM</th>
                      <th style={S.refillTh}>Fuel Station</th>
                      <th style={S.refillTh}>Litres</th>
                      <th style={S.refillTh}>Price/L</th>
                      <th style={S.refillTh}>Amount</th>
                      <th style={S.refillTh}>Payment</th>
                      <th style={S.refillTh}>Paid By</th>
                      <th style={S.refillTh}>Route</th>
                      <th style={S.refillTh}>Remarks</th>
                      <th style={S.refillTh}>Supervisor disposition</th>
                      <th style={S.refillTh}>Additional</th>
                      <th style={S.refillTh}>Slip</th>
                      <th
                        style={{
                          ...S.refillTh,
                          textAlign: "center",
                          minWidth: isMobile ? `${PREFILL_MOBILE_ACTION_COL_PX}px` : "210px",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRefills.map((r, idx) => {
                      const rowBusy = pendingAction && pendingAction.id === r._id;
                      const prefBtn = (action, label, tone) => {
                        const working = rowBusy && pendingAction?.action === action;
                        const cls =
                          tone === "y"
                            ? "diesel-vm-prefill-btn diesel-vm-prefill-btn--y"
                            : "diesel-vm-prefill-btn diesel-vm-prefill-btn--b";
                        return (
                          <button
                            key={action}
                            type="button"
                            className={cls}
                            disabled={rowBusy}
                            aria-busy={working ? "true" : "false"}
                            onClick={() => updatePendingRefill(r._id, action)}
                          >
                            {working ? "Working…" : label}
                          </button>
                        );
                      };
                      return (
                      <tr
                        key={r._id || idx}
                        className="diesel-vm-prefill-row"
                        style={{ backgroundColor: vmRecordRowBg(idx) }}
                      >
                        <td style={S.refillTd}>
                          {r.date ? new Date(r.date).toLocaleDateString("en-IN") : "—"}
                        </td>
                        <td style={S.refillTd}>{r.time || "—"}</td>
                        <td style={{ ...S.refillTd, fontWeight: "bold" }}>{r.vehicleNumber || "—"}</td>
                        <td style={S.refillTd}>{r.vehicleType || "—"}</td>
                        <td style={S.refillTd}>{r.ownerName || "—"}</td>
                        <td style={S.refillTd}>{r.scheduleMileagePerL ?? "—"}</td>
                        <td style={S.refillTd}>{r.scheduleRefillDays ?? "—"}</td>
                        <td style={S.refillTd}>{r.scheduleLtrs ?? "—"}</td>
                        <td style={S.refillTd}>{r.expectKm ?? "—"}</td>
                        <td style={S.refillTd}>{r.driverName || "—"}</td>
                        <td style={S.refillTd}>{r.startKm ?? "—"}</td>
                        <td style={S.refillTd}>{r.endKm ?? "—"}</td>
                        <td style={S.refillTd}>{r.totalKm ?? "—"}</td>
                        <td style={S.refillTd}>{r.pumpName || "—"}</td>
                        <td style={S.refillTd}>{r.volume ?? "—"}</td>
                        <td style={S.refillTd}>{r.ratePerLiter ?? "—"}</td>
                        <td style={S.refillTd}>
                          {r.totalAmount != null ? `₹${Number(r.totalAmount).toLocaleString("en-IN")}` : "—"}
                        </td>
                        <td style={S.refillTd}>{r.paymentMode || "—"}</td>
                        <td style={S.refillTd}>{r.paidBy || "—"}</td>
                        <td style={S.refillTd}>{r.vehicleRoute || "—"}</td>
                        <td style={S.refillTd}>{r.remarks || "—"}</td>
                        <td style={S.refillTd}>{r.supervisorDisposition || "—"}</td>
                        <td style={S.refillTd}>{r.additional || "—"}</td>
                        <td style={S.refillTd}>
                          {r.dieselSlipPhoto ? (
                            <ViewMediaEyeAnchor href={r.dieselSlipPhoto} ariaLabel="View diesel slip" title="View slip in new tab" />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td
                          style={{
                            ...S.refillTd,
                            ...S.refillActionCell,
                            position: "relative",
                            verticalAlign: "top",
                          }}
                        >
                          <div className="diesel-vm-prefill-actions-shell" data-prefill-actions-wrap>
                            <button
                              type="button"
                              className={
                                "diesel-vm-prefill-actions-trigger" +
                                (prefillActionsOpenId === r._id
                                  ? " diesel-vm-prefill-actions-trigger--open"
                                  : "")
                              }
                              aria-busy={rowBusy ? "true" : "false"}
                              disabled={rowBusy}
                              aria-expanded={prefillActionsOpenId === r._id}
                              aria-controls={`prefill-act-panel-${r._id}`}
                              id={`prefill-act-toggle-${r._id}`}
                              onClick={() =>
                                setPrefillActionsOpenId((open) => (open === r._id ? null : r._id))
                              }
                            >
                              <span className="diesel-vm-prefill-sr-only">
                                {prefillActionsOpenId === r._id ? "Hide actions" : "Show actions"}
                              </span>
                              {rowBusy ? (
                                <span aria-hidden="true">…</span>
                              ) : (
                                <FaEye aria-hidden />
                              )}
                            </button>
                            <div
                              id={`prefill-act-panel-${r._id}`}
                              className={
                                "diesel-vm-prefill-actions diesel-vm-prefill-actions-panel" +
                                (prefillActionsOpenId === r._id
                                  ? " diesel-vm-prefill-actions-panel--open"
                                  : "")
                              }
                              style={S.refillActionsWrap}
                              role="group"
                              aria-labelledby={`prefill-act-toggle-${r._id}`}
                              aria-hidden={prefillActionsOpenId !== r._id}
                            >
                              {prefBtn("approve", "Approve", "y")}
                              {prefBtn("deny", "Deny", "b")}
                              {prefBtn("mismatch", "Mismatch", "b")}
                              {prefBtn("confirm", "Confirm", "y")}
                              {prefBtn("wrong_entry", "Wrong entry", "b")}
                            </div>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="diesel-vm-prefill-empty" role="status">
                No pending refill requests from Supervisor.
              </div>
            )}
          </div>
        </div>

        <div style={S.divider} />

        <div style={S.sectionTitle}>Diesel Records</div>

        <div style={{
          ...S.formGrid,
          marginBottom: "12px",
          padding: isMobile ? "12px" : "14px 16px",
          backgroundColor: "#fafafa",
          border: "1px solid #e8e8e8",
          borderRadius: "6px",
        }}>
          <div>
            <label style={S.label}>Date filter:</label>
            <select style={S.input} value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}>
              <option value="all">All dates</option>
              <option value="day">Day wise</option>
              <option value="month">Monthly</option>
              <option value="year">Year</option>
              <option value="range">From — To</option>
            </select>
          </div>

          {filterMode === "day" ? (
            <div>
              <label style={S.label}>Pick date:</label>
              <input type="date" style={S.input} value={filterDay}
                onChange={(e) => setFilterDay(e.target.value)} />
            </div>
          ) : null}

          {filterMode === "month" ? (
            <div>
              <label style={S.label}>Month:</label>
              <input type="month" style={S.input} value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)} />
            </div>
          ) : null}

          {filterMode === "year" ? (
            <div>
              <label style={S.label}>Year:</label>
              <select style={S.input} value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}>
                <option value="">Any year</option>
                {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
          ) : null}

          {filterMode === "range" ? (
            <>
              <div>
                <label style={S.label}>From:</label>
                <input type="date" style={S.input} value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)} />
              </div>
              <div>
                <label style={S.label}>To:</label>
                <input type="date" style={S.input} value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)} />
              </div>
            </>
          ) : null}

          <div>
            <label style={S.label}>Vehicle No:</label>
            <FleetVehicleSelect
              name="_filterVehicle"
              value={filterVehicleNo}
              onChange={(e) => setFilterVehicleNo(e.target.value)}
              style={S.input}
              placeholderOption="All vehicles"
            />
          </div>

          <div>
            <label style={S.label}>Driver / quick search:</label>
            <input style={S.input}
              placeholder="Search…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", flexWrap: "wrap" }}>
            <button type="button" style={S.btnBlack} onClick={clearRecordFilters}>
              Clear filters
            </button>
            <button type="button" style={S.btnBlack} onClick={downloadCSV}>⬇ CSV</button>
          </div>
          <label style={{ ...S.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={hideDirectDieselEntries}
              onChange={(e) => setHideDirectDieselEntries(e.target.checked)}
            />
            Official refills only (hide admin direct saves)
          </label>
        </div>

        <div style={{ fontSize: "13px", color: "#555", marginBottom: "10px" }}>
          Showing <b>{filtered.length}</b> of <b>{records.length}</b> record{records.length === 1 ? "" : "s"}
        </div>

        <div style={vmTableScrollWrap}>
          <table style={S.table}>
            {isMobile ? vmRecordColGroupReact(DIESEL_RECORD_HEADERS.length) : null}
            <thead>
              <tr>
                {DIESEL_RECORD_HEADERS.map((h, hi) => <th style={S.th} key={hi}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((r, i) => {
                const eKm  = recExpKm(r);
                const aKmL = recMileage(r);
                const sc   = scheduleMap[r.vehicleNumber] || {};
                const diff = eKm && r.totalKm ? parseFloat(r.totalKm) - eKm : null;
                const perfStr = recPerfKmL(r);
                const perfNum = parseFloat(perfStr);

                return (
                  <tr key={i} style={{ backgroundColor: vmRecordRowBg(i) }}>
                    <td style={S.td}>{r.date ? new Date(r.date).toLocaleDateString("en-IN") : "—"}</td>
                    <td style={S.td}>{r.time || "—"}</td>
                    <td style={{ ...S.td, fontWeight: "bold" }}>{r.vehicleNumber}</td>
                    <td style={S.td}>{r.vehicleType || sc.vehicleType || "—"}</td>
                    <td style={S.td}>{r.ownerName || sc.ownerName || "—"}</td>
                    <td style={{ ...S.td, color: "#6a1b9a" }}>
                      {sc.scheduleKmPerLitre != null && sc.scheduleKmPerLitre !== "" ? `${sc.scheduleKmPerLitre}` : "—"}
                    </td>
                    <td style={S.td}>
                      {sc.scheduleInterval != null && sc.scheduleInterval !== "" ? `${sc.scheduleInterval}` : "—"}
                    </td>
                    <td style={S.td}>
                      {sc.scheduleLitres != null && sc.scheduleLitres !== "" ? `${sc.scheduleLitres}` : "—"}
                    </td>
                    <td style={{ ...S.td, color: "#0277bd" }}>{eKm != null ? `${eKm}` : "—"}</td>
                    <td style={S.td}>{r.driverName}</td>
                    <td style={S.td}>{fmtKmCell(r.startKm)}</td>
                    <td style={S.td}>{fmtKmCell(r.endKm)}</td>
                    <td style={S.td}>{fmtKmCell(r.totalKm)}</td>
                    <td style={S.td}>{r.pumpName || "—"}</td>
                    <td style={S.td}>{r.volume} L</td>
                    <td style={S.td}>₹{r.ratePerLiter}</td>
                    <td style={S.td}><b>₹{Number(r.totalAmount ?? r.amount ?? 0).toLocaleString("en-IN")}</b></td>
                    <td style={S.td}>{r.paymentMode}</td>
                    <td style={S.td}>{r.paidBy || "—"}</td>
                    <td style={{
                      ...S.td, fontWeight: "bold",
                      color: sc.scheduleKmPerLitre && aKmL !== "—"
                        ? parseFloat(aKmL) >= parseFloat(sc.scheduleKmPerLitre) ? "#2e7d32" : "#c62828"
                        : "#000",
                    }}>{aKmL !== "—" ? `${aKmL} km/L` : "—"}</td>
                    <td style={{
                      ...S.td, fontWeight: "bold",
                      color: diff !== null ? (diff >= 0 ? "#2e7d32" : "#c62828") : "#aaa",
                    }}>
                      {diff !== null ? `${diff >= 0 ? "+" : ""}${Math.round(diff)} km` : "—"}
                    </td>
                    <td style={{
                      ...S.td, fontWeight: "bold",
                      color: perfStr !== "—" && !Number.isNaN(perfNum) && perfNum >= 0
                        ? "#2e7d32"
                        : perfStr !== "—" && !Number.isNaN(perfNum)
                          ? "#c62828"
                          : "#aaa",
                    }}>{perfStr !== "—" ? `${perfStr} km/L` : "—"}</td>
                    <td style={S.td}>{r.vehicleRoute || "—"}</td>
                    <td style={S.td}>
                      {r.dieselSlipPhoto ? (
                        <ViewMediaEyeAnchor href={r.dieselSlipPhoto} ariaLabel="View diesel slip" title="View slip in new tab" />
                      ) : "—"}
                    </td>
                    <td style={S.td}>{r.remarks || "—"}</td>
                    <td style={S.td}>{r.supervisorDisposition || "—"}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={DIESEL_RECORD_HEADERS.length}
                    style={{ ...S.td, textAlign: "center", color: "#aaa", padding: "28px" }}>
                    {records.length === 0
                      ? "No diesel records yet"
                      : "No records match the current filters"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default Diesel;